// ============================================================
// VISION TIMELINE — Fase 9.13
// Timeline histórica de snapshots e eventos visuais
// ============================================================
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Camera, Calendar, ChevronLeft, ChevronRight, Play, Eye } from 'lucide-react'
import { useVisionStore } from '@/store/visionStore'
import { VisionEventRow, VisionTimelineCard } from '@/design-system/vision'
import { getRecentSnapshots } from '@/core/cameras/snapshotScheduler'
import { TEXT, SURFACE } from '@/design-system/tokens'
import type { CameraSnapshot } from '@/types/vision'

interface Props {
  obraId:   string
  compact?: boolean
}

type ViewMode = 'events' | 'snapshots' | 'mixed'

export default function VisionTimeline({ obraId, compact = false }: Props) {
  const { events, selectedCameraId } = useVisionStore()
  const [snapshots, setSnapshots] = useState<CameraSnapshot[]>([])
  const [viewMode, setViewMode]   = useState<ViewMode>('mixed')
  const [daysBack, setDaysBack]   = useState(1)
  const [loading, setLoading]     = useState(false)

  useEffect(() => {
    setLoading(true)
    getRecentSnapshots(obraId, 40)
      .then(data => {
        const filtered = selectedCameraId
          ? data.filter(s => s.camera_id === selectedCameraId)
          : data
        setSnapshots(filtered)
      })
      .finally(() => setLoading(false))
  }, [obraId, selectedCameraId, daysBack])

  const filteredEvents = events.filter(e => {
    if (e.obra_id !== obraId) return false
    if (selectedCameraId && e.camera_id && e.camera_id !== selectedCameraId) return false
    const since = Date.now() - daysBack * 86_400_000
    return new Date(e.ts).getTime() > since
  })

  // Build unified timeline
  type Entry = { type: 'event' | 'snapshot'; ts: string; data: any }
  const entries: Entry[] = [
    ...filteredEvents.map(e => ({ type: 'event' as const, ts: e.ts, data: e })),
    ...(viewMode !== 'events' ? snapshots.map(s => ({ type: 'snapshot' as const, ts: s.taken_at, data: s })) : []),
  ].sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      {!compact && (
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {/* View mode */}
          <div className="flex gap-1">
            {(['mixed', 'events', 'snapshots'] as ViewMode[]).map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                className="text-[9px] font-bold px-2 py-1 rounded-lg capitalize"
                style={{
                  background: viewMode === m ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.04)',
                  color:      viewMode === m ? 'hsl(24 95% 55%)' : TEXT.muted,
                  border:     `1px solid ${viewMode === m ? 'rgba(249,115,22,0.25)' : 'rgba(255,255,255,0.06)'}`,
                }}>
                {m}
              </button>
            ))}
          </div>

          {/* Day selector */}
          <div className="flex items-center gap-1 ml-auto">
            <button onClick={() => setDaysBack(d => Math.max(1, d - 1))}
              className="p-1 rounded" style={{ color: TEXT.muted }}>
              <ChevronLeft size={12} />
            </button>
            <span className="text-[10px]" style={{ color: TEXT.muted }}>
              Últimas {daysBack * 24}h
            </span>
            <button onClick={() => setDaysBack(d => Math.min(30, d + 1))}
              className="p-1 rounded" style={{ color: TEXT.muted }}>
              <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto space-y-0">
        {loading && (
          <div className="text-center py-4 text-xs" style={{ color: TEXT.faint }}>
            Carregando...
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 gap-2 opacity-30">
            <Camera size={24} />
            <span className="text-sm">Sem registros visuais neste período</span>
          </div>
        )}

        <AnimatePresence mode="popLayout">
          {entries.map((entry, i) => (
            <motion.div
              key={`${entry.type}-${i}`}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.02 }}
            >
              {entry.type === 'event' ? (
                <VisionEventRow event={entry.data} />
              ) : (
                <VisionTimelineCard snapshot={entry.data} />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Stats footer */}
      {!compact && (
        <div className="mt-2 pt-2 flex gap-4 text-[9px]"
          style={{ borderTop: '1px solid rgba(255,255,255,0.04)', color: TEXT.faint }}>
          <span><Eye size={8} className="inline mr-1" />{filteredEvents.length} eventos</span>
          <span><Camera size={8} className="inline mr-1" />{snapshots.length} snapshots</span>
        </div>
      )}
    </div>
  )
}
